<template>
  <v-card
    class="transparent"
  >
    <v-banner
      icon="mdi-arrow-decision-outline"
      single-line
    >
      <h2>Workflows</h2>

      <template #actions>
        <div
          v-if="skippableWorkflows"
          class="d-flex"
        >
          <span v-if="enableWorkflows">Workflows are enabled</span>
          <span v-else>Workflows will be skipped</span>
          <v-switch
            v-model="enableWorkflows"
            class="ma-0 pa-0 pl-4"
            hide-details
          />
        </div>
      </template>
    </v-banner>

    <v-form
      :disabled="!editableWorkflows"
    >
      <div
        v-for="(workflow, index) of currentWorkflows"
        :key="workflow.id"
      >
        <v-container
          fluid
        >
          <v-row>
            <v-col cols="12">
              <v-container fluid>
                <v-row>
                  <v-col cols="6">
                    <DateField
                      v-model="workflow.date"
                      hide-details
                      label="Date"
                      outlined
                      @input="date => updateInDaysForWorkflow(index, date)"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      :value="getDocketEntryTypeName(workflow.docket_entry_type_to_create_id)"
                      class="font-weight-bold"
                      disabled
                      hide-details
                      :label="getDocketEntryTypeLabel(workflow.docket_entry_type_to_create_id)"
                      outlined
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-textarea
                      v-model="workflow.comment"
                      auto-grow
                      clearable
                      hide-details
                      label="With comment"
                      outlined
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="6">
                    <UserSelect
                      v-if="docketEntryTypeAssignable(workflow)"
                      v-model="workflow.assignees"
                      clearable
                      :extra-options="specialUserTypeOptions"
                      hide-details
                      hide-disabled
                      hide-selected
                      label="assigned to"
                      multiple
                      outlined
                    />
                  </v-col>
                  <v-col
                    v-if="workflow.notifications"
                    cols="6"
                  >
                    <v-badge
                      bordered
                      class="d-block"
                      color="warning"
                      icon="mdi-alert-outline"
                      overlap
                      :value="workflow.requiresAttention"
                    >
                      <v-btn
                        block
                        color="secondary"
                        depressed
                        large
                        @click="_ => openWorkflowNotificationsEditor(workflow)"
                      >
                        {{ workflow.notifications.length ? workflow.notifications.length : 'No' }}
                        {{ workflow.notifications.length === 1 ? 'Notification' : 'Notifications' }} Selected
                      </v-btn>
                    </v-badge>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                    class="text-right"
                    cols="12"
                  >
                    <v-btn
                      v-if="currentWorkflows.length > 1"
                      color="red"
                      :disabled="!userCanEditWorkflows"
                      outlined
                      @click="_ => onRemoveRow(index)"
                    >
                      <v-icon left>
                        delete
                      </v-icon>
                      Remove Workflow
                    </v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-col>
          </v-row>
        </v-container>
        <v-divider />
      </div>

      <div
        v-if="enableWorkflows && userCanEditWorkflows"
        class="d-flex justify-center py-8"
      >
        <v-btn
          color="warning"
          outlined
          @click="_ => onReset()"
        >
          <v-icon left>
            mdi-restore
          </v-icon>
          Reset Workflows
        </v-btn>
      </div>
    </v-form>

    <div
      v-if="!edited.opened"
      class="overlay-parent d-flex justify-center"
    >
      <div class="pt-16">
        <v-alert
          border="left"
          max-width="400"
          outlined
          prominent
          text
          type="error"
        >
          Please select a date on Overview.
        </v-alert>
      </div>
    </div>

    <DocketEntryNotificationsEditorDialog
      v-if="notificationsEditor.visible"
      v-model="notificationsEditor.visible"
      :docket-entry="workflowDocketEntry"
      :parties="edited.parties"
      @save="notifications => saveWorkflowNotifications(notifications)"
    />
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { cloneDeep } from 'lodash'
import moment from 'moment-timezone'

import Action from '@/data/Permissions/enums.json'
import { alertHandler } from '@/alertHandler'
import { rules } from '@/utilities/validation-rules'
import { specialUserTypeOptions } from '@/data/Workflow'

import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'
import { USER_NAMESPACE } from '@/store/user'

import DateField from '@/components/Fields/DateField.vue'
import DocketEntryNotificationsEditorDialog from '@/components/DocketEntry/DocketEntryNotificationsEditorDialog.vue'
import UserSelect from '@/components/Fields/UserSelect.vue'

export default {
  name: 'LegalCaseEntryEditorWorkflow',

  components: {
    DateField,
    DocketEntryNotificationsEditorDialog,
    UserSelect,
  },

  props: {
    // eslint-disable-next-line vue/no-unused-properties
    valid: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      daysPositionOptions: ['before', 'after'],
      notificationsEditor: {
        visible: false,
        workflow: null,
      },

      rules,
    }
  },

  computed: {
    currentWorkflows () {
      if (!this.edited.modified_workflows) return []

      for (const workflow of this.edited.modified_workflows) {
        workflow.date = moment(this.edited.opened).add(workflow.in_days, 'days').format('YYYY-MM-DD')
      }

      return this.edited.modified_workflows
    },

    editableWorkflows () {
      const checkPermission = this.getWorkflowEditors.find(editor => editor.includes('have_permission'))

      if (checkPermission) {
        return this.userCanEditWorkflows || this.can(Action.DocketEntry.Workflow.Edit)
      }

      return this.userCanEditWorkflows
    },

    enableWorkflows: {
      get () { return !this.edited.skip_workflow },
      set (val) { this.edited.skip_workflow = !val },
    },

    legalCaseType () {
      return this.legalCaseTypes.find(lt => lt.id === this.edited.legal_case_type_id)
    },

    requiresAttention () {
      return this.enableWorkflows && this.currentWorkflows.some(cw => cw.requiresAttention)
    },

    skippableWorkflows () {
      const checkPermission = this.getWorkflowSkippers.find(editor => editor.includes('have_permission'))

      if (checkPermission) {
        return this.userCanSkipWorkflows || this.can(Action.DocketEntry.Workflow.Skip)
      }

      return this.userCanSkipWorkflows
    },

    specialUserTypeOptions: _ => specialUserTypeOptions,

    workflowDocketEntry () {
      return {
        action_date: this.notificationsEditor.workflow.date,
        action_type: this.notificationsEditor.workflow.action_type,
        docket_entry_notifications: this.notificationsEditor.workflow.notifications,
        docket_entry_type_id: this.notificationsEditor.workflow.docket_entry_type_to_create_id,
        event_time_end: null,
        event_time_start: null,
        legal_case_id: null,
        timezone: this.features?.timezone?.timezone,
      }
    },

    ...mapGetters([
      'legalCaseTypes',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'edited',
      'original',
    ]),

    ...mapGetters(SHARED_NAMESPACE, [
      'features',
      'getWorkflowEditors',
      'getWorkflowSkippers',
      'userCanEditWorkflows',
      'userCanSkipWorkflows',
    ]),

    ...mapGetters(USER_NAMESPACE, [
      'can',
    ]),
  },

  watch: {
    'edited.opened' (newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.resetAllNotifications()
      }
    },

    'edited.parties' () {
      this.resetAllNotifications()
    },

    enableWorkflows (val) {
      if (!val) this.edited.modified_workflows = []
      else this.onReset()
    },

    legalCaseType: {
      handler (newVal, oldVal) {
        if (oldVal?.id === newVal?.id) return

        if (!newVal) {
          this.clearWorkflowSharedDetails()
          this.edited.modified_workflows = []
        } else {
          this.fetchWorkflowSharedDetails({ id: newVal.id, model: 'legal_case_types' })
          this.onReset()
        }
      },

      immediate: true,
    },

    requiresAttention: {
      handler (value) {
        this.$emit('update:warning', value)
      },

      immediate: true,
    },
  },

  async created () {
    await this.getDocketEntryTypes()
    await this.getLegalCaseTypesModel()
    this.onReset()
  },

  methods: {
    docketEntryTypeAssignable (workflow) {
      if (!this.docketEntryTypes || !workflow.docket_entry_type_to_create_id) return false

      const actionTypeSelected =
        this.docketEntryTypes.find(d => d.id === workflow.docket_entry_type_to_create_id)

      return actionTypeSelected?.action_type?.toLowerCase() === 'task'
    },

    getDocketEntryTypeLabel (docketEntryTypeId) {
      const det = this.docketEntryTypes?.find(det => det.id === docketEntryTypeId)
      return `Create ${det?.action_type}`
    },

    getDocketEntryTypeName (docketEntryTypeId) {
      return this.docketEntryTypes?.find(det => det.id === docketEntryTypeId)?.name
    },

    legalCaseTypeAssignable (workflow) {
      if (!this.legalCaseTypes || !workflow.docket_entry_type_to_create_id) return false

      const actionTypeSelected =
        this.docketEntryTypes.find(d => d.id === workflow.docket_entry_type_to_create_id)

      return actionTypeSelected?.action_type?.toLowerCase() === 'task'
    },

    onRemoveRow (index) {
      this.edited.modified_workflows.splice(index, 1)
    },

    onReset () {
      if (this.edited.id && this.original.docket_entry_type_id === this.edited.docket_entry_type_id) {
        this.edited.modified_workflows = cloneDeep(this.original.modified_workflows)
      } else {
        this.edited.modified_workflows = cloneDeep(this.legalCaseType?.workflows.
          map(w => ({ ...w, requiresAttention: false })) || [])
      }

      this.resetAllNotifications()
    },

    openWorkflowNotificationsEditor (workflow) {
      this.notificationsEditor.workflow = workflow
      this.notificationsEditor.visible = true
    },

    resetAllNotifications () {
      for (const workflow of this.currentWorkflows) {
        const docketEntryType = this.docketEntryTypes?.find(det => det.id === workflow.docket_entry_type_to_create_id)
        workflow.action_type = docketEntryType?.action_type

        if (docketEntryType?.docket_entry_type_notifications?.length) {
          workflow.notifications = []
          workflow.requiresAttention = true
        }
      }
    },

    saveWorkflowNotifications (notifications) {
      this.notificationsEditor.workflow.notifications = notifications.map(n => {
        return { ...n, trigger_at: n.trigger_at || moment().format() }
      })
      this.notificationsEditor.workflow.requiresAttention = false
      this.notificationsEditor.workflow = null
    },

    setNotificationsRequireAttention (value) {
      this.edited.modified_workflows?.forEach(mw => {
        if (mw.notifications?.length) mw.requiresAttention = value
      })
    },

    updateInDaysForWorkflow (index, date) {
      if (!date || !this.edited.opened) return
      const refDate = moment(this.edited.opened, 'YYYY-MM-DD')
      const inDays = moment(date, 'YYYY-MM-DD').diff(refDate, 'days')

      this.edited.modified_workflows[index].in_days = inDays
    },

    ...mapActions([
      'getLegalCaseTypesModel',
    ]),

    ...mapActions(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'getDocketEntryTypes',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'clearWorkflowSharedDetails',
      'fetchWorkflowSharedDetails',
    ]),
  },
}
</script>

<style scoped>
.overlay-parent {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.8);
}
</style>
